import { useEffect } from 'react';
import { useTenant } from '@/hooks/useTenant';

const CUSTOM_SCRIPT_ATTR = 'data-provider-custom-script';

const removeInjectedScripts = () => {
  document
    .head
    .querySelectorAll(`[${CUSTOM_SCRIPT_ATTR}]`)
    .forEach((node) => node.remove());
};

const cloneWithScripts = (source: Element): Element => {
  if (source.tagName.toLowerCase() !== 'script') {
    return source.cloneNode(true) as Element;
  }

  const script = document.createElement('script');
  for (const { name, value } of Array.from(source.attributes)) {
    script.setAttribute(name, value);
  }
  script.text = source.textContent ?? '';
  return script;
};

const appendCustomScripts = (raw: string): Element[] => {
  const template = document.createElement('template');
  template.innerHTML = raw;

  const appended: Element[] = [];
  Array.from(template.content.childNodes).forEach((node) => {
    if (!(node instanceof Element)) {
      return;
    }
    const element = cloneWithScripts(node);
    element.setAttribute(CUSTOM_SCRIPT_ATTR, 'true');
    document.head.appendChild(element);
    appended.push(element);
  });

  return appended;
};

export function CustomScriptsInjector() {
  const { customScripts } = useTenant();

  useEffect(() => {
    removeInjectedScripts();

    const scripts = typeof customScripts === 'string' ? customScripts.trim() : '';
    if (!scripts) {
      return () => {
        removeInjectedScripts();
      };
    }

    const appended = appendCustomScripts(scripts);

    return () => {
      appended.forEach((node) => node.remove());
      removeInjectedScripts();
    };
  }, [customScripts]);

  return null;
}
